import React, { useEffect, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import TextInput from "../../common/textfield/TextInput";
import SelectFiled from "../../common/textfield/SelectFiled";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import {
  QuestionnaireFormikObjEdit,
  questionnaireForOptions,
  questionnaireTagOptions,
} from "./Constant";
import {
  editQuestionnaire,
  deleteQuestionnaire,
  getAPLRules,
  deleteAPLRules,
  updateAPLRules,
  createAPLRules,
  createQuestionnaire,
} from "../../api/Questionnaire";
import { NavigateNext, ExpandMore, ArrowBack, Help } from "@mui/icons-material";
import { Skeleton, Tooltip } from "@mui/material";
import "./style.css";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  TableCell,
} from "@mui/material";
import { useGetQuestionsByCategoryQuery } from "../../../hooks/ReactQueryHooks/useGetQuestionsByCategoryQuery";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { useSelector } from "react-redux";
import DeleteIcon from "../../../assets/images/cross-icon.png";
import { ViewDate } from "../../../utils/DateSupport";
import { useLocation, useNavigate } from "react-router-dom";
import { Table } from "react-bootstrap";
import APLRuleModalWindow from "./APLRuleModalWindow";
import EditIcon from "../../../assets/images/edit.png";
import DeleteIconHome from "../../../assets/images/delete.png";
import { useGetOrgLicensedProducts } from "../../../hooks/ReactQueryHooks/useGetOrgLicensedProducts";
import { getOrganization } from "../../api/Organization";
import { FullPageSpinner } from "../../common/Spinner/FullPageSpinner";

const EditQuestionnaire = () => {
  const location = useLocation();
  const { initialState, isReadOnly, actionType, createCopy } = location.state || {};
  const questionnaireEditType = actionType; // "CREATE" | "UPDATE" | "VIEW"
  const navigate = useNavigate();

  const [btnLoading, setBtnLoading] = useState(false);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [categorySelectedQuestions, setCategorySelectedQuestions] = useState([]);
  const [intialCategorySelectedQuestions, setIntialCategorySelectedQuestions] = useState([]);
  const [questionsCategoryList, setQuestionsCategoryList] = useState();
  const [err, setErr] = useState("");
  const { getConfirmation } = useConfirmDialog();
  const [deleting, setDeleting] = useState(false);

  const userType = useSelector((s) => s?.auth?.user?.["cognito:groups"]);
  const userOrg = useSelector((s) => s?.auth?.user?.["custom:orgId"]);
  const permissions = useSelector((s) => s?.auth?.user?.permissions);
  const userData = useSelector((s) => s?.auth?.user);

  const hasAccess = userType?.includes("Super-Admin") ? true : !!!initialState?.isDefault;

  // ---------- ORG selection ----------
  const [organizations, setOrganizations] = useState([]);
  const originalOrgId = initialState?.orgId || userOrg;
  const [selectedOrgId, setSelectedOrgId] = useState(originalOrgId);

  useEffect(() => {
    (async () => {
      try {
        const res = await getOrganization(); // list orgs user can view
        setOrganizations(res?.data || []);
      } catch {
        // ignore
      }
    })();
  }, []);

  const orgOptions = (organizations || []).map((o) => ({
    name: o?.name || o?.display || o?.orgName || o?.id,
    value: o?.id,
  }));

  const handleOrgChange = (e) => {
    const newOrgId = e?.target?.value;
    setSelectedOrgId(newOrgId);
    formik.setFieldValue("selectedOrgId", newOrgId);
    setRuleSet([]);                   // rules are org-scoped; clear until reload
    setErr("");
  };

  // ---------- APL Rules ----------
  const [isRulesLoading, setIsRulesLoading] = useState(true);
  const [rules, setRules] = useState([]);      // previous rules snapshot (for diff)
  const [APLRuleModal, setAPLRuleModal] = useState(false);
  const [APLModalAction, setAPLModalAction] = useState("CREATE");
  const [currentEditingRule, setCurrentEditingRule] = useState(null);
  const [ruleSet, setRuleSet] = useState([]);  // current UI rules
  const [draggedRowIndex, setDraggedRowIndex] = useState(null);
  const { data: orgLicensedProducts } = useGetOrgLicensedProducts({});
  const isContactCenterActive = orgLicensedProducts?.callCenter;

  // ---------- Form ----------
  const formik = useFormik({
    ...QuestionnaireFormikObjEdit,
    initialValues: {
      ...(QuestionnaireFormikObjEdit?.initialValues || {}),
      selectedOrgId: selectedOrgId || "",
      purpose: initialState?.purpose || "clinical-visit",
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      values.title = values.title?.trim();
      values.purpose = values.purpose?.trim();
      values.note = values.note?.trim();
      const trimmedTag = values.questionnaireTag?.trim() || "";
      values.questionnaireTag = trimmedTag === "none" ? "" : trimmedTag;
      const tempQuestionsArr = [];
      const questionCounts = {
        LOCATION: 0,
        REASON: 0,
        TELEPHONE: 0,
        CHANNEL: 0,
        RELATIONSHIP: 0,
        SELFCALLING: 0,
        CONSENT: 0,
        CALLER: 0,
        COMPLIANCE: 0,
        INCLINATION: 0,
      };

      categorySelectedQuestions.forEach((q) => {
        tempQuestionsArr.push({
          text: q.question,
          id: q?.id,
          required: !!q.required,
          question: q.question,
          answerType: q.answerType,
          answerOption: q.answerOption || q.options,
          note: q.note,
          tag: q.tag || "",
          questionId: q?.id || null,
        });
        if (questionCounts.hasOwnProperty(q.tag)) questionCounts[q.tag] += 1;
      });

      // validations
      if (formik.values?.purpose?.includes("getcare")) {
        const req = ["REASON", "LOCATION", "TELEPHONE", "CHANNEL"];
        const missing = validateMissingQuestions(req, questionCounts);
        if (missing.length) return setErr(`Must Select These Questions: ${missing.join(", ")}`);
        if (!validateDuplicateQuestions(req, questionCounts)) return;
      }
      if (formik.values?.purpose?.includes("telephone-intake")) {
        const req = ["REASON", "LOCATION", "TELEPHONE", "RELATIONSHIP", "SELFCALLING", "CONSENT", "CALLER"];
        const missing = validateMissingQuestions(req, questionCounts);
        if (missing.length) return setErr(`Must Select These Questions: ${missing.join(", ")}`);
        if (!validateDuplicateQuestions([...req, "INCLINATION"], questionCounts)) return;
      }
      if (formik.values?.purpose?.includes("clinical-visit")) {
        const req = ["COMPLIANCE"]
        if(categorySelectedQuestions.length === 0) return setErr("Must Select a Question");
        if (!validateDuplicateQuestions(req, questionCounts)) return;
      }

      setBtnLoading(true);
      values.isDefault = values.isDefault ? values.isDefault : false;

      const movedOrg = questionnaireEditType !== "CREATE" && selectedOrgId !== originalOrgId;

      try {
        const apiRes =
          questionnaireEditType === "CREATE"
            ? await createQuestionnaire({
                ...values,
                orgId: selectedOrgId,          // create in selected org
                question: tempQuestionsArr,
                status: "active",
              })
            : await editQuestionnaire(initialState?.id, {
                ...values,
                orgId: selectedOrgId,          // allow changing org on UPDATE
                question: tempQuestionsArr,
              });

        success(apiRes.message);
        const qid = questionnaireEditType === "CREATE" ? apiRes?.data?.id : initialState?.id;

        if (isContactCenterActive) {
          if (movedOrg) {
            // migrate rules from old org -> new org
            try { await deleteAPLRules(qid, originalOrgId); } catch {}
            if (ruleSet?.length > 0) {
              await createAPLRules(qid, selectedOrgId, {
                orgId: selectedOrgId,
                createdBy: {
                  reference: `Practitioner/${userData?.["custom:practitioner_id"]}`,
                  display: userData?.name?.[0]?.text,
                },
                rules: ruleSet,
                questionnaireId: qid,
              });
            }
          } else {
            // same org: reconcile
            if (rules?.length > 0 && ruleSet?.length === 0) {
              try { await deleteAPLRules(qid, selectedOrgId); } catch {}
            } else if (rules?.length === 0 && ruleSet?.length > 0) {
              await createAPLRules(qid, selectedOrgId, {
                orgId: selectedOrgId,
                createdBy: {
                  reference: `Practitioner/${userData?.["custom:practitioner_id"]}`,
                  display: userData?.name?.[0]?.text,
                },
                rules: ruleSet,
                questionnaireId: qid,
              });
            } else if (rules?.length > 0 && ruleSet?.length > 0) {
              await updateAPLRules(qid, selectedOrgId, {
                orgId: selectedOrgId,
                updatedBy: {
                  reference: `Practitioner/${userData?.["custom:practitioner_id"]}`,
                  display: userData?.name?.[0]?.text,
                },
                rules: ruleSet,
                questionnaireId: qid,
              });
            }
          }
        }
        setBtnLoading(false);
        // Show overlay loading for 3 seconds then navigate
        setOverlayLoading(true);
        setTimeout(() => {
          setOverlayLoading(false);
          navigate(-1);
        }, 3000);
      } catch (res) {
        failed(res?.response?.data?.message || res?.response?.data?.error || res.message);
        setBtnLoading(false);
        return;
      }
    },
  });

  function validateMissingQuestions(requiredTags, counts) {
    const label = (tag) =>
      ({
        LOCATION: "Patient Location",
        REASON: "Reason for visit",
        TELEPHONE: "Return Telephone Number",
        CHANNEL: "Preferred Channel",
        RELATIONSHIP: "Relationship to Patient",
        SELFCALLING: "Calling for Yourself",
        CONSENT: "Patient Consent",
        CALLER: "Caller Name",
      }[tag]);
    return requiredTags.filter((t) => counts[t] === 0).map(label);
  }

  function validateDuplicateQuestions(tags, counts) {
    const label = (tag) =>
      ({
        LOCATION: "Patient Location",
        REASON: "Reason for visit",
        TELEPHONE: "Return Telephone Number",
        CHANNEL: "Preferred Channel",
        RELATIONSHIP: "Relationship to Patient",
        SELFCALLING: "Calling for Yourself",
        CONSENT: "Patient Consent",
        CALLER: "Caller Name",
        INCLINATION: "Patient Inclination",
        COMPLIANCE: "Patient Compliance",
      }[tag]);
    for (const t of tags) {
      if (counts[t] > 1) {
        setErr(`You Have Selected More Than One Question Marked With ${label(t)}`);
        return false;
      }
    }
    return true;
  }

  // Load categories/questions for selected org & purpose
  const onSuccess = (data) => {
    setQuestionsCategoryList(data);
    // initial load retains existing items; org/purpose change clears above
    if (intialCategorySelectedQuestions?.length) {
      setCategorySelectedQuestions(intialCategorySelectedQuestions);
      setIntialCategorySelectedQuestions([]);
    }
    setErr("");
  };

  const { isLoading, isFetching } = useGetQuestionsByCategoryQuery({
    onSuccess,
    purpose: formik?.values?.purpose || initialState?.purpose,
    orgId: selectedOrgId,
    refetchOnWindowFocus: false,
  });

  // Reset selected questions when org or purpose changes (for edit UX you asked for)
  useEffect(() => {
    // when switching org/purpose during edit, start fresh
    if (questionnaireEditType !== "VIEW") {
      setCategorySelectedQuestions([]);
      setErr("");
    }
  }, [formik.values?.purpose, questionnaireEditType]);

  // Pre-fill form + selected org + items
  useEffect(() => {
    if (!initialState) return;
    if (initialState?.orgId && questionnaireEditType === "CREATE") {
      setSelectedOrgId(userOrg);              // auto-show questionnaire org
      formik.setFieldValue("selectedOrgId", userOrg);
    } else if(initialState?.orgId) {
      setSelectedOrgId(initialState.orgId);              // auto-show questionnaire org
      formik.setFieldValue("selectedOrgId", initialState.orgId);
    }
    formik.setFieldValue(
      "title",
      questionnaireEditType === "CREATE" ? "" : initialState?.title || ""
    );
    formik.setFieldValue("purpose", initialState?.purpose || "");
    formik.setFieldValue(
      "questionnaireTag",
      questionnaireEditType === "CREATE" && !createCopy
        ? "none"
        : initialState?.questionnaireTag || "none"
    );
    formik.setFieldValue("time", initialState?.duration?.value);
    formik.setFieldValue("isDefault", initialState?.isDefault || "");
    setIntialCategorySelectedQuestions(
      initialState?.item?.map((it) => ({ 
        ...it, 
        question: it?.text,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState]);

  // Fetch APL rules for selected org
  useEffect(() => {
    if (!isContactCenterActive || !initialState?.id) return;
    setIsRulesLoading(true);
    getAPLRules(initialState.id, selectedOrgId)
      .then((res) => {
        if (res?.status === true || res?.status === "true") {
          questionnaireEditType !== "CREATE" && setRules(res?.data?.[0]?.rules || []);
          setRuleSet(res?.data?.[0]?.rules || []);
        } else {
          setRules([]); setRuleSet([]);
        }
      })
      .catch((res) => failed(res?.message))
      .finally(() => setIsRulesLoading(false));
  }, [isContactCenterActive, initialState?.id, selectedOrgId, questionnaireEditType]);

  const handleSelectedQuestions = (question) => {
    setErr("");
    if (isReadOnly) return;
    // if (!categorySelectedQuestions?.some((i) => i?.question === question?.question)) {
    if (!categorySelectedQuestions?.some((i) => i?.id === question?.id)) {   
      setCategorySelectedQuestions(([question, ...categorySelectedQuestions]));
    } else {
      failed("Question is already part of this questionnaire.")
    //   setCategorySelectedQuestions(categorySelectedQuestions.filter((i) => i?.question !== question?.question));
    }
  };

  // const handleToggleExcludeFromExtReports = (index) => {
  //   if (isReadOnly) return;
  //   const updated = [...categorySelectedQuestions];
  //   updated[index] = {
  //     ...updated[index],
  //     excludeFromExtReports: !updated[index].excludeFromExtReports
  //   };
  //   setCategorySelectedQuestions(updated);
  // };

  const isSafeToDelete = (questionId) => {
    const found = ruleSet?.some((rule) => {
    // Check if questionId is in target-question-id array
      const isTarget = Array.isArray(rule["target-question-id"]) &&
      rule["target-question-id"].includes(questionId);

    // Check if questionId matches any trigger-question-id in conditions
      const isTrigger = Array.isArray(rule.conditions) &&
      rule.conditions.some((condition) => condition["trigger-question-id"] === questionId);

      return isTarget || isTrigger;
    });

    return !found;
  };

  const handleDrop = (droppedItem) => {
    setErr("");
    if (!droppedItem.destination) return;
    const updated = [...categorySelectedQuestions];
    const [reordered] = updated.splice(droppedItem.source.index, 1);
    updated.splice(droppedItem.destination.index, 0, reordered);
    setCategorySelectedQuestions(updated);
  };

  const handleDelete = async (id, entryName, purposeData) => {
    const confirmed = await getConfirmation({
      title: "Attention!",
      entryName,
      actionBtnTitle: "Delete",
    });
    if (!confirmed) return;
    setDeleting(initialState?.id);
    localStorage.setItem("lastDeletedQuestionnaireId", initialState?.id);
    const payload = { status: "retired", purpose: purposeData, type: "questionnaire" };
    deleteQuestionnaire(initialState?.id, payload)
      .then(() => {
          navigate("/app/manage-questionnaire");
          success("Questionnaire Deleted!");  
        })
      .catch((res) => failed(res?.response?.data?.message || res?.response?.data?.error || res.message))
      .finally(() => setDeleting(false));
  };

  const handleCancelClick = async (entryName) => {
    const confirmed = await getConfirmation({
      title: "Attention!",
      entryName,
      actionBtnTitle: "Yes",
      message: "Are you sure you would like to ",
    });
    if (confirmed) navigate(-1);
  };

  const handleDeleteQuestion = (index, item) => {
    if (!isSafeToDelete(item?.id)) {
      failed("There is an active rule in place for this question. Remove rule and try again");
      return;
    }
    setErr("");
    const updated = [...categorySelectedQuestions];
    updated.splice(index, 1);
    setCategorySelectedQuestions(updated);
  };

  if (!initialState) return null;

  return (
    <section className="common-listing">
      {overlayLoading && <FullPageSpinner loadingText="Saving..." />}
      {APLRuleModal && (
        <APLRuleModalWindow
          onShow={APLRuleModal}
          onHide={() => setAPLRuleModal(false)}
          action={APLModalAction}
          data={categorySelectedQuestions}
          questionnaireId={initialState.id}
          existingRuleData={ruleSet}
          currentEditingRule={currentEditingRule}
          handleModalSubmit={() => {}}
        />
      )}
      <div className="heading-wrap mb-3">
        <Tooltip title="Go back">
          <div
            style={{ marginRight: "1rem", cursor: "pointer" }}
            onClick={() => navigate(-1)}
          >
            <ArrowBack />
          </div>
        </Tooltip>
      </div>
      <div className="custom-card p-4">
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <Row>
            <Col>
              <SelectFiled
                keyField={"selectedOrgId"}
                label={"Organization"}
                formik={formik}
                placeholder={"Select Organization"}
                options={orgOptions}
                onChange={handleOrgChange}
                value={selectedOrgId}
                disabled={createCopy ? false : true}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <TextInput
                disabled={isReadOnly}
                keyField={"title"}
                label={"Title"}
                formik={formik}
                placeholder={"Title"}
              />
            </Col>
            <Col>
              <SelectFiled
                disabled={true}
                keyField={"purpose"}
                label={"Workflow"}
                formik={formik}
                placeholder={"Select"}
                options={questionnaireForOptions}
                value={formik.values?.purpose || initialState?.purpose}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <SelectFiled
                disabled={isReadOnly}
                keyField={"questionnaireTag"}
                label={"Questionnaire Tag"}
                formik={formik}
                placeholder={"Select"}
                options={questionnaireTagOptions}
                value={formik.values?.questionnaireTag ?? initialState?.questionnaireTag ?? "none"}
                required={false}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <h4>Available Categories</h4>
              {isLoading || isFetching ? (
                <ul className="questionnaire-list">
                  {[1, 2, 3, 4, 5, 6, 7]?.map((val) => (
                    <li key={val}>
                      <Skeleton width={"100%"} animation="wave" />
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="questionnaire-list">
                  {questionsCategoryList?.map((category) => (
                    <Accordion key={category?.id}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>{category?.categoryName}</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ padding: 0 }}>
                        <ul className="questionnaire-list">
                          {[...(category?.questions || [])]
                            .sort((a, b) => (a?.question || "").localeCompare(b?.question || ""))
                            .map((question) => (
                              <li
                                key={question?.linkId}
                                onClick={() => handleSelectedQuestions(question)}
                              >
                                {question?.question}
                                <NavigateNext style={{ float: "right" }} />
                              </li>
                            ))}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </ul>
              )}
            </Col>

            <Col>
              <h4>Selected Questions</h4>
              {categorySelectedQuestions?.length ? null : (
                <div>No available question selected</div>
              )}
              <Tooltip title={"Drag to Reorder Questions"}>
                <ul className="questionnaire-list">
                  {!isReadOnly ? (
                    <DragDropContext onDragEnd={handleDrop}>
                      <Droppable droppableId="list-container">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {categorySelectedQuestions.map((item, index) => (
                              <Draggable
                                key={item?.question}
                                draggableId={item?.question}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <li
                                    key={index}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{ 
                                      display: "flex", 
                                      alignItems: "center", 
                                      justifyContent: "space-between", 
                                      gap: "10px",
                                      ...provided.draggableProps.style,
                                      backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'transparent'
                                    }}
                                  >
                                    <div 
                                      style={{ flex: 1, cursor: 'grab' }}
                                      {...provided.dragHandleProps}
                                    >
                                      <span> {item?.question} </span>
                                    </div>
                                    <img
                                      src={DeleteIcon}
                                      alt="Delete"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteQuestion(index, item);
                                      }}
                                      style={{ cursor: "pointer" }}
                                    />
                                  </li>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  ) : (
                    categorySelectedQuestions?.map((item, key) => (
                      <li key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                        <div style={{ flex: 1 }}><span>{item.text || item.question}</span></div>
                      </li>
                    ))
                  )}
                </ul>
              </Tooltip>
              <div style={{ fontSize: "14px", color: "#dc3545", addingTop: "5px" }}>
                {err}
              </div>
            </Col>
          </Row>

          <hr />

          {(isContactCenterActive && permissions?.includes("Manage Questionnaire")) && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  marginBottom: "10px",
                  gap: "10px",
                }}
              >
                <h4 style={{ marginRight: "auto" }}>
                  <Tooltip title="The answer to the Trigger Question determines when the Target Question is enabled.">
                    <span style={{ display: "inline-flex", alignItems: "center", cursor: "default" }}>
                      Rules <Help fontSize="small" />
                    </span>
                  </Tooltip>
                </h4>
                {(questionnaireEditType === "CREATE" ||
                  questionnaireEditType === "UPDATE") && (
                  <Button
                    onClick={() => {
                      setAPLModalAction("CREATE");
                      setAPLRuleModal(true);
                    }}
                    isLoading={btnLoading}
                    style={{ padding: "5px 10px" }}
                  >
                    Create Rule
                  </Button>
                )}
              </div>
              <div className="table-wrap">
                <Table>
                  <thead>
                    <tr>
                      <th>Index</th>
                      <th>Description</th>
                      <th>
                        <Tooltip title="The Action will be disabled by default and enabled when the Trigger Question's answer evaluates to true.">
                          <span style={{ display: "inline-flex", alignItems: "center", cursor: "default" }}>
                            Action <Help fontSize="small" />
                          </span>
                        </Tooltip>
                      </th>
                      {questionnaireEditType !== "VIEW" && <th style={{textAlign: "center"}}>Configure</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {isRulesLoading ? (
                      Array.from({ length: 5 }).map((_, rowIndex) => (
                        <tr key={rowIndex}>
                          {Array(6)
                            .fill()
                            .map((_, colIndex) => (
                              <td key={colIndex}>
                                <Skeleton animation="wave" />
                              </td>
                            ))}
                        </tr>
                      ))
                    ) : ruleSet?.length > 0 ? (
                      ruleSet.map((rule, index) => {
                        const triggerItem = initialState?.item.find(
                          (it) => it.id === rule?.["trigger-question-id"]
                        );

                        const targets = Array.isArray(rule?.["target-question-id"])
                          ? rule?.["target-question-id"]
                              .map((tid) => initialState?.item.find((it) => it.id === tid))
                              .filter(Boolean)
                          : (rule?.["target-question-id"]
                              ? [initialState?.item.find((it) => it.id === rule?.["target-question-id"])].filter(Boolean)
                              : []);
                        const targetText = targets.map((t) => t.text).join("<br>");

                        return (
                            <tr
                              key={rule?.id}
                              draggable
                              title="Drag to reorder rules"
                              onDragStart={() => setDraggedRowIndex(index)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => {
                                const draggedRule = ruleSet[draggedRowIndex];
                                const updated = [...ruleSet];
                                updated.splice(draggedRowIndex, 1);
                                updated.splice(index, 0, draggedRule);
                                setRuleSet(updated.map((r, i) => ({ ...r, id: i + 1 })));
                              }}
                            >
                              <td>{rule?.id}</td>
                              <td>{rule?.description}</td>
                              <td>
                                {targetText.split("<br>").map((line, i) => (
                                  <React.Fragment key={i}>
                                    {line}
                                    <br />
                                  </React.Fragment>
                                ))}
                              </td>
                              {questionnaireEditType !== "VIEW" && (
                                <td>
                                  <div className="action-wrap">
                                    <Tooltip title="Edit">
                                      <div
                                        onClick={() => {
                                          setCurrentEditingRule(rule);
                                          setAPLModalAction("UPDATE");
                                          setAPLRuleModal(true);
                                        }}
                                        className="success-btn"
                                      >
                                        <img src={EditIcon} alt="Edit" />
                                      </div>
                                    </Tooltip>
                                    <Tooltip title={"Delete"}>
                                      <div
                                        onClick={async () => {
                                          const confirmed = await getConfirmation({
                                            title: "Attention!",
                                            entryName: "delete rule",
                                            actionBtnTitle: "Yes",
                                            message: "Are you sure you would like to ",
                                          });
                                          if (!confirmed) return;
                                          const transformed = ruleSet
                                            .filter((r) => r.id !== rule.id)
                                            .map((r, i) => ({ ...r, id: i + 1 }));
                                          setRuleSet(transformed);
                                        }}
                                        className="delete-btn"
                                      >
                                        <img src={DeleteIconHome} alt="Delete" />
                                      </div>
                                    </Tooltip>
                                  </div>
                                </td>
                              )}
                            </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <TableCell colSpan={"8"} style={{ textAlign: "center" }}>
                          No Data Available
                        </TableCell>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          <hr />
          <Row>
            {questionnaireEditType !== "CREATE" && (
              <Col>
                <div>
                  <h6>Date Created : {ViewDate(initialState?.createdAt)}</h6>
                  <h6>Date Last Updated : {ViewDate(initialState?.updatedAt)}</h6>
                </div>
              </Col>
            )}
            <Col>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {!isReadOnly ? (
                  <>
                    <div>
                      <Row>
                        <Button
                          variant="secondary"
                          title="Cancel"
                          onClick={() => handleCancelClick("cancel editing questionnaire")}
                        >
                          Cancel
                        </Button>

                        <Button
                          type="submit"
                          isLoading={btnLoading}
                          style={{ marginLeft: "10px" }}
                        >
                          {questionnaireEditType === "CREATE" ? "Create" : "Update"}
                        </Button>

                        {questionnaireEditType === "UPDATE" && (
                          <Button
                            variant="secondary"
                            title="Delete"
                            onClick={() =>
                              hasAccess &&
                              (deleting
                                ? null
                                : handleDelete(
                                    initialState?.id,
                                    initialState?.title,
                                    initialState?.purpose
                                  ))
                            }
                            style={{ marginLeft: "10px" }}
                          >
                            Delete
                          </Button>
                        )}
                      </Row>
                    </div>
                  </>
                ) : null}
              </div>
            </Col>
          </Row>

          <div>
            {formik?.touched["question"] && formik?.errors["question"] ? (
              <div className="error-text">{formik?.errors["question"]}</div>
            ) : null}
          </div>
        </form>
      </div>
    </section>
  );
};

export default EditQuestionnaire;
